/**
 * Menu Items Configured Fiscal Setup Page
 * Lists menu items that already have a complete EBM fiscal setup (tax
 * category, item code, and classification code) at an outlet under RRA
 * fiscal obligation — sourced from menu_items_configured_fiscal_setup_view,
 * the inverse of menu_items_missing_fiscal_setup_view. Kept as a separate
 * page (rather than a filter toggle) so "still needs work" and "already
 * done" stay clearly distinguishable at a glance. Rows remain editable via
 * the same FiscalSetupRowEditor used on the Missing Fiscal Setup page.
 */

import { useMenuItemsConfiguredFiscalSetup } from '@hooks/menu';
import { useAppSelector } from '@hooks/useRedux';
import { queryKeys } from '@lib/queryKeys';
import { selectCurrentUser } from '@store/auth/authSelectors';
import { selectAuthOutletId, selectEffectiveOutletId } from '@store/outlets';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Search } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { syncItemClasses } from '@/renderer/services/ebmItemClassificationService';
import { supabase } from '@/renderer/services/supabase';
import { MenuItemConfiguredFiscalSetup } from '@/renderer/types/menu';
import { apiError, apiSuccess } from '@/renderer/util/notify';
import { Input, OutletIndicator } from '@components/common';
import { PaginationControls } from '@components/common/PaginationControls';
import { useDebounce } from '@hooks/useDebounce';
import { FiscalSetupRowEditor } from './FiscalSetupRowEditor';
import { SyncItemClassesButton } from './SyncItemClassesButton';

const PAGE_SIZE = 20;

interface FiscalTaxCategoryOption {
  id: number;
  code: string;
  name: string;
}

export const MenuItemsConfiguredFiscalSetupPage: React.FC = () => {
  const queryClient = useQueryClient();
  const currentUser = useAppSelector(selectCurrentUser);
  const outletId = useAppSelector(selectEffectiveOutletId);
  const authOutletId = useAppSelector(selectAuthOutletId);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [categories, setCategories] = useState<FiscalTaxCategoryOption[]>([]);

  const params = useMemo(
    () => ({ outletId, page, pageSize: PAGE_SIZE, search: debouncedSearch }),
    [outletId, page, debouncedSearch],
  );

  const { data, isLoading, isFetching, refetch } =
    useMenuItemsConfiguredFiscalSetup(params);

  const countryIso = currentUser?.company?.country_iso;

  useEffect(() => {
    if (!countryIso) return;
    const loadCategories = async () => {
      const { data: rows, error } = await supabase
        .from('fiscal_tax_categories')
        .select('id, code, name')
        .eq('country_iso', countryIso)
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      if (!error && rows) setCategories(rows as FiscalTaxCategoryOption[]);
    };
    loadCategories();
  }, [countryIso]);

  const rows: MenuItemConfiguredFiscalSetup[] = data?.data ?? [];
  const isCompanyWide = !outletId;

  const handleRowSaved = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.menu.configuredFiscalSetup(params),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.menu.missingFiscalSetup(params),
    });
  };

  const handleSyncItemClasses = async () => {
    if (!authOutletId) {
      apiError('Connect this device to an outlet before syncing.');
      return;
    }
    const result = await syncItemClasses(authOutletId);
    if (!result.success || !result.data) {
      apiError(result.error || 'Failed to sync item classification codes.');
      return;
    }
    apiSuccess(
      `Synced ${result.data.syncedCount} item classification code${
        result.data.syncedCount === 1 ? '' : 's'
      } from RRA.`,
    );
  };

  return (
    <div className="space-y-6">
      <OutletIndicator description="Menu items with a complete EBM fiscal setup — tax category, item code, and classification code." />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            Configured Fiscal Setup
          </h1>
          <p className="text-sm text-gray-500">
            {data?.pagination.totalItems ?? 0} menu item
            {data?.pagination.totalItems === 1 ? '' : 's'} fully configured
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SyncItemClassesButton onSync={handleSyncItemClasses} />
          <button
            type="button"
            onClick={() => refetch()}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search
          size={16}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <Input
          placeholder="Search by item name"
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          className="pl-9"
        />
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <div className="max-h-[32rem] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
              <tr className="text-left">
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Item
                </th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Outlet
                </th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Tax Category
                </th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Item Code
                </th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Classification Code
                </th>
                <th className="px-5 py-3" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td
                    colSpan={isCompanyWide ? 6 : 5}
                    className="px-5 py-10 text-center text-sm text-gray-500"
                  >
                    Loading…
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={isCompanyWide ? 6 : 5}
                    className="px-5 py-10 text-center text-sm text-gray-500"
                  >
                    No menu items have a complete fiscal setup yet.
                  </td>
                </tr>
              )}
              {rows.map((item) => (
                <FiscalSetupRowEditor
                  key={item.menu_item_id}
                  item={item}
                  categories={categories}
                  onSaved={handleRowSaved}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {data && (
        <PaginationControls
          pagination={data.pagination}
          itemLabel="items"
          onPageChange={setPage}
        />
      )}
    </div>
  );
};

export default MenuItemsConfiguredFiscalSetupPage;
